import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    Trash2,
    ChevronDown,
    ChevronUp,
    Image as ImageIcon,
    Clock,
    RefreshCw,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UserUpload {
    id: string;
    file_path: string;
    file_name: string;
    public_url: string;
    created_at: string;
    expires_at: string;
}

interface RecentUploadsProps {
    onSelectImage: (url: string) => void;
}

const RecentUploads = ({ onSelectImage }: RecentUploadsProps) => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const { toast } = useToast();

    const [uploads, setUploads] = useState<UserUpload[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<UserUpload | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchUploads = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from("user_uploads" as any)
                .select("*")
                .eq("user_id", user.id)
                .gt("expires_at", new Date().toISOString())
                .order("created_at", { ascending: false })
                .limit(20);

            if (error) throw error;
            setUploads((data as any[]) || []);
        } catch (err) {
            console.error("Failed to fetch uploads:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user && isOpen) {
            fetchUploads();
        }
    }, [user, isOpen]);

    const handleDelete = async (upload: UserUpload) => {
        setDeleting(true);
        try {
            // Delete from storage
            const { error: storageError } = await supabase.storage
                .from("generation-inputs")
                .remove([upload.file_path]);

            if (storageError) {
                console.warn("Storage delete failed:", storageError);
            }

            // Delete from database
            const { error: dbError } = await supabase
                .from("user_uploads" as any)
                .delete()
                .eq("id", upload.id);

            if (dbError) throw dbError;

            setUploads((prev) => prev.filter((u) => u.id !== upload.id));
            toast({
                title: t("ลบสำเร็จ", "Deleted"),
                description: t("ลบรูปภาพเรียบร้อยแล้ว", "Image deleted successfully"),
            });
        } catch (err: any) {
            toast({
                variant: "destructive",
                title: t("ลบไม่สำเร็จ", "Delete failed"),
                description: err.message,
            });
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    const getDaysRemaining = (expiresAt: string) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diff = expires.getTime() - now.getTime();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };

    if (!user) return null;

    return (
        <>
            <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
                <CollapsibleTrigger asChild>
                    <Button
                        variant="ghost"
                        className="w-full justify-between px-3 py-2 h-auto text-sm text-muted-foreground hover:text-foreground"
                    >
                        <span className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t("รูปที่อัพโหลดล่าสุด", "Recent Uploads")}
                            {uploads.length > 0 && (
                                <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                    {uploads.length}
                                </span>
                            )}
                        </span>
                        {isOpen ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </Button>
                </CollapsibleTrigger>

                <CollapsibleContent className="pt-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-4">
                            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                    ) : uploads.length === 0 ? (
                        <div className="text-center py-4 text-xs text-muted-foreground">
                            {t(
                                "ยังไม่มีรูปที่อัพโหลด (เก็บไว้ 7 วัน)",
                                "No uploads yet (kept for 7 days)"
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 gap-2">
                            {uploads.map((upload) => (
                                <div
                                    key={upload.id}
                                    className="relative group aspect-square rounded-lg overflow-hidden border border-border bg-muted/20 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                                    onClick={() => onSelectImage(upload.public_url)}
                                >
                                    <img
                                        src={upload.public_url}
                                        alt={upload.file_name}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />

                                    {/* Days remaining badge */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                                        <div className="flex items-center gap-1 text-[10px] text-white/80">
                                            <Clock className="h-2.5 w-2.5" />
                                            {getDaysRemaining(upload.expires_at)}d
                                        </div>
                                    </div>

                                    {/* Delete button */}
                                    <Button
                                        variant="destructive"
                                        size="icon"
                                        className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteTarget(upload);
                                        }}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    <p className="text-[10px] text-muted-foreground text-center mt-2">
                        {t(
                            "คลิกเพื่อใช้ซ้ำ | รูปจะถูกลบอัตโนมัติหลัง 7 วัน",
                            "Click to reuse | Auto-deleted after 7 days"
                        )}
                    </p>
                </CollapsibleContent>
            </Collapsible>

            {/* Delete confirmation dialog */}
            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={() => setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t("ยืนยันการลบ", "Confirm Delete")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t(
                                "คุณต้องการลบรูปภาพนี้หรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้",
                                "Are you sure you want to delete this image? This action cannot be undone."
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>
                            {t("ยกเลิก", "Cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteTarget && handleDelete(deleteTarget)}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleting
                                ? t("กำลังลบ...", "Deleting...")
                                : t("ลบ", "Delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default RecentUploads;
